
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Save, Loader2, Plus, X, Check, Search } from 'lucide-react';
import { ShowcaseContent } from '../../pages/project-showcase/types';
import { defaultShowcaseContent } from '../../pages/project-showcase/constants/defaults';

export default function ShowcaseManager() {
    const [content, setContent] = useState<ShowcaseContent | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [projectCategories, setProjectCategories] = useState<string[]>([]);
    const [newCategoryLabel, setNewCategoryLabel] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // 1. Fetch site content
            const { data: contentData } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'projects_showcase')
                .maybeSingle();

            if (contentData) {
                setContent(contentData.content as ShowcaseContent);
            } else {
                setContent(defaultShowcaseContent);
            }

            // 2. Fetch all unique categories from projects table
            const { data: projectsData } = await supabase
                .from('projects')
                .select('details');

            const uniqueCats = new Set<string>();
            projectsData?.forEach(p => {
                const details = p.details || {};
                if (details.category) uniqueCats.add(details.category);
                if (Array.isArray(details.categories)) {
                    details.categories.forEach((c: string) => uniqueCats.add(c));
                }
            });
            setProjectCategories(Array.from(uniqueCats));

        } catch (err) {
            console.error('Error fetching showcase data:', err);
            setMessage({ type: 'error', text: 'Failed to load data' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!content) return;
        setSaving(true);
        setMessage(null);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'projects_showcase',
                    content: content,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setMessage({ type: 'success', text: 'Showcase settings saved!' });
        } catch (err) {
            console.error('Error saving showcase content:', err);
            setMessage({ type: 'error', text: 'Failed to save settings' });
        } finally {
            setSaving(false);
        }
    };

    const toggleCategory = (label: string) => {
        if (!content) return;

        const id = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const exists = content.showcaseCategories.some(c => c.id === id);

        let newCategories;
        if (exists) {
            newCategories = content.showcaseCategories.filter(c => c.id !== id);
        } else {
            newCategories = [...content.showcaseCategories, { id, label }];
        }

        setContent({ ...content, showcaseCategories: newCategories });
    };

    const addCustomCategory = () => {
        if (!newCategoryLabel.trim() || !content) return;

        const label = newCategoryLabel.trim();
        const id = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

        if (content.showcaseCategories.some(c => c.id === id)) {
            setNewCategoryLabel('');
            return;
        }

        setContent({
            ...content,
            showcaseCategories: [...content.showcaseCategories, { id, label }]
        });
        setNewCategoryLabel('');
    };

    if (loading || !content) {
        return (
            <div className="flex justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Portfolio Showcase Manager</h2>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save Settings
                </button>
            </div>

            {message && (
                <div className={`rounded-md p-4 ${message.type === 'success' ? 'bg-green-900/30 text-green-400 border border-green-900' : 'bg-red-900/30 text-red-400 border border-red-900'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Text Content */}
                <div className="space-y-6 bg-gray-900 p-6 rounded-xl border border-gray-800">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Header Content</h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Showcase Title</label>
                            <input
                                type="text"
                                value={content.showcaseTitle}
                                onChange={(e) => setContent({ ...content, showcaseTitle: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Introductory Paragraph</label>
                            <textarea
                                rows={6}
                                value={content.showcaseSubtitle}
                                onChange={(e) => setContent({ ...content, showcaseSubtitle: e.target.value })}
                                className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Category Selection */}
                <div className="space-y-6 bg-gray-900 p-6 rounded-xl border border-gray-800">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Filter Categories</h3>

                    <div className="space-y-6">
                        {/* Selected Categories */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-3  tracking-wider">Active Filters (Visible on site)</label>
                            <div className="flex flex-wrap gap-2">
                                {content.showcaseCategories.map((cat) => (
                                    <div key={cat.id} className="flex items-center gap-1 bg-blue-600/20 text-blue-400 border border-blue-600/30 px-3 py-1.5 rounded-full text-xs font-semibold">
                                        {cat.label}
                                        <button
                                            onClick={() => setContent({ ...content, showcaseCategories: content.showcaseCategories.filter(c => c.id !== cat.id) })}
                                            className="ml-1 hover:text-white"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                                {content.showcaseCategories.length === 0 && (
                                    <p className="text-xs text-gray-600 italic">No filters active. "All" will still be shown.</p>
                                )}
                            </div>
                        </div>

                        {/* Selection List */}
                        <div className="pt-4 border-t border-gray-800">
                            <label className="block text-xs font-medium text-gray-500 mb-3  tracking-wider">Choose from Projects</label>
                            <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                {projectCategories.map((label) => {
                                    const id = label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                                    const isActive = content.showcaseCategories.some(c => c.id === id);
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => toggleCategory(label)}
                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? 'bg-gray-800 text-white' : 'bg-gray-800/30 text-gray-500 hover:bg-gray-800/50 hover:text-gray-300'
                                                }`}
                                        >
                                            <span>{label}</span>
                                            {isActive ? <Check size={16} className="text-blue-500" /> : <Plus size={16} />}
                                        </button>
                                    );
                                })}
                                {projectCategories.length === 0 && (
                                    <p className="text-xs text-gray-600">No categories found in current projects.</p>
                                )}
                            </div>
                        </div>

                        {/* Add Custom */}
                        <div className="pt-4 border-t border-gray-800">
                            <label className="block text-xs font-medium text-gray-500 mb-2  tracking-wider">Add Custom Category</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newCategoryLabel}
                                    onChange={(e) => setNewCategoryLabel(e.target.value)}
                                    placeholder="e.g. Embedded Systems"
                                    className="flex-1 bg-gray-800 border-gray-700 rounded-md text-white px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                    onClick={addCustomCategory}
                                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded-md text-xs font-bold"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
